import { isInteger } from "@foretagsplatsen/core-helpers/number";
import RepositoryContext from "./RepositoryContext.js";

const buildContext = (contextData) =>
	new RepositoryContext({ extraProperties: contextData });

const buildModel = ({ data, context, materializer }) => {
	if (!materializer || !materializer.materialize || !data) {
		return data;
	}

	return materializer.materialize(data, context);
};

const buildModels = ({ dataArray, context, materializer }) => {
	if (!materializer) {
		return dataArray;
	}

	if (materializer.materializeAll) {
		return materializer.materializeAll(dataArray, context);
	}

	return dataArray.map((data) => buildModel({ data, context, materializer }));
};

/**
 * @typedef Materializer
 * @type {object}
 * @property {function(object, RepositoryContext):object} materialize -
 *   This function is called with a serialized model and a context
 *   as argument and should return a materialized version of the data
 * @property {function(Array, RepositoryContext):object} [materializeAll] -
 *   This function is called with an array of serialized models and a
 *   context as argument and should return an array of materialized
 *   versions of the data. If absent, `materialize` is used instead
 */

/**
 * Convert `data` into an object using `materializer` and passing the
 * `materializer` a context.
 *
 * The `context` object is first converted into a `RepositoryContext`
 * before being passed to the materializer.
 *
 * @param {object | Array} data - What to materialize. If it is an
 * array, each element of the array will be materialized.
 * @param {object} context - Key/value pairs to pass to the
 * materializer. The context is useful to provide additional data
 * useful for materialization.
 * @param {Materializer} materializer - The object responsible for the
 * materialization.
 */
export default function materialize({
	data,
	context: rawContext,
	materializer,
}) {
	const context = buildContext(rawContext);

	if (isODataPage(data)) {
		return materializeODataPage({ data, context, materializer });
	}

	return Array.isArray(data)
		? buildModels({ dataArray: data, context, materializer })
		: buildModel({ data, context, materializer });
}

/**
 * Return a materializer (used by {@link Repository} for its `model`
 * constructor parameter) that detects if fetched data contains OData
 * groups of models or just a model or models.
 *
 * `modelMaterializer` is used to materialize OData group's values as
 * well as models coming outside of OData groups. When the fetched
 * data contains OData groups, `keyMaterializer` is used to
 * materialize the group's keys.
 *
 * @param {Materializer} key - The materializer to use for
 *   the keys of OData groups
 * @param {Materializer} value - The materializer to use for the
 *   values of OData groups and when there is no OData group
 */
export function makeKeyValueMaterializer({ key, value }) {
	return {
		materialize: (...rest) => value.materialize(...rest),
		materializeAll: (dataArray, context) => {
			if (isGroupDataArray(dataArray)) {
				return materializeAllGroups({
					groupKeyMaterializer: key,
					groupValueMaterializer: value,
					dataArray,
					context,
				});
			}
			return buildModels({
				dataArray,
				context,
				materializer: value,
			});
		},
	};
}

/**
 * Materialize `dataArray`, which should contain OData groups, with `context`.
 *
 * @param {Materializer} groupKeyMaterializer - The materializer to use for
 *   the key of each item in `dataArray`
 * @param {Materializer} groupValueMaterializer - The materializer to
 *   use for the values of each item in `dataArray`
 * @param {{key:object,value:object[]}[]} dataArray - Array of
 *   {key,value} groups
 */
export function materializeAllGroups({
	groupKeyMaterializer,
	groupValueMaterializer,
	dataArray,
	context,
}) {
	return buildModels({
		dataArray,
		context,
		materializer: {
			materialize: (data) => ({
				key: buildModel({
					data: data.key,
					context,
					materializer: groupKeyMaterializer,
				}),
				value: buildModels({
					dataArray: data.value,
					context,
					materializer: groupValueMaterializer,
				}),
			}),
		},
	});
}

function isGroupDataArray(dataArray = []) {
	return (
		dataArray &&
		dataArray.length > 0 &&
		dataArray[0].key !== undefined &&
		dataArray[0].value?.length
	);
}

function isODataPage(data) {
	if (!data) return false;

	const keys = Object.keys(data);

	return (
		keys.length === 2 &&
		keys.includes("value") &&
		keys.includes("@odata.Count") &&
		Array.isArray(data["value"]) &&
		isInteger(data["@odata.Count"])
	);
}

function materializeODataPage({ data, context, materializer }) {
	return {
		...data,
		value: buildModels({ dataArray: data.value, context, materializer }),
	};
}
